<template>
  <Modal v-model="open" :title="title" width="820" class="goods-add-modal" scrollable>
    <div class="goods-modal-body">
    <Form
      ref="formItem"
      :model="formItem"
      :label-width="88"
      :rules="ruleValidate"
    >
      <FormItem label="商品名称" prop="name" required>
        <Input v-model="formItem.name" placeholder="请输入商品名称，不可与已有商品重复" clearable />
      </FormItem>
      <FormItem label="价格" prop="price" required>
        <Input v-model="formItem.price" placeholder="请输入价格" clearable />
      </FormItem>
      <FormItem label="会员价">
        <Input v-model="formItem.member_price" placeholder="可选，低于原价时生效" clearable />
        <div class="json-field-help">留空表示无会员价；需在「会员权益」中开启会员价开关后结算才会套用。</div>
      </FormItem>
      <FormItem label="库存" prop="num" required>
        <Input v-model="formItem.num" placeholder="请输入剩余库存" clearable />
        <div class="json-field-help">
          多 SKU 时，保存后<strong>总库存 = 各 SKU 库存之和</strong>；仅改此处不会自动分摊到多行，请在下方逐行填写每 SKU 库存。
          若只有<strong>一条</strong> SKU，保存时会用此处库存与该 SKU 对齐。
        </div>
      </FormItem>
      <FormItem label="描述" prop="desc" required>
        <Input v-model="formItem.desc" placeholder="请输入描述" clearable />
      </FormItem>
      <FormItem label="售卖范围" prop="fulfillment_scope">
        <Select v-model="formItem.fulfillment_scope" transfer placeholder="请选择">
          <Option value="dine_in">仅堂食</Option>
          <Option value="both">全部（等同堂食）</Option>
        </Select>
        <div class="json-field-help">
          当前产品仅堂食；请选「仅堂食」。历史「外卖」范围商品不会在小程序展示。
        </div>
      </FormItem>
      <FormItem label="搜索词" prop="search_keywords">
        <Input
          v-model="formItem.search_keywords"
          type="textarea"
          :autosize="{ minRows: 2, maxRows: 4 }"
          placeholder="多个词用逗号、空格或分号分隔，用于列表搜索"
        />
        <div class="json-field-help">
          除商品名称、描述外，额外能被搜到的词；可不填。
        </div>
      </FormItem>
      <Divider>商超：卡片标签 / 规格 / SKU</Divider>
      <Alert type="info" show-icon style="margin-bottom: 12px">
        用下面表单逐步填写即可，保存时会自动补全 <code>sku_id</code>、空的规格 id 等；与小程序、后台清洗逻辑一致。
        不需要时标签可留空；单规格商品选「单规格」即可不写 SKU。
      </Alert>

      <FormItem label="卡片标签">
        <div v-for="(row, ti) in tagRows" :key="'tag-' + ti" class="shop-row-block">
          <Row :gutter="8" type="flex" align="middle">
            <Col :span="9">
              <Input v-model="row.text" placeholder="标签文字，如：热卖、限时" maxlength="48" clearable />
            </Col>
            <Col :span="7">
              <Select v-model="row.tone" placeholder="颜色" transfer>
                <Option value="">默认（灰）</Option>
                <Option value="green">绿</Option>
                <Option value="blue">蓝</Option>
                <Option value="grey">灰</Option>
                <Option value="gold">金</Option>
                <Option value="red">红</Option>
                <Option value="orange">橙</Option>
              </Select>
            </Col>
            <Col :span="6">
              <Select v-model="row.kind" placeholder="类型" transfer>
                <Option value="caption">caption（常用）</Option>
                <Option value="badge">badge</Option>
                <Option value="line">line</Option>
                <Option value="rank">rank</Option>
                <Option value="discount">discount</Option>
              </Select>
            </Col>
            <Col :span="2">
              <Button type="text" ghost @click="removeTagRow(ti)">
                <Icon type="md-trash" />
              </Button>
            </Col>
          </Row>
        </div>
        <Button type="dashed" long icon="md-add" @click="addTagRow">添加标签</Button>
        <div class="json-field-help">展示在商品卡片角标，最多 10 条；可不添加。</div>
      </FormItem>

      <FormItem label="规格维度">
        <Checkbox v-model="specUseSchema">启用多规格说明（与下方 SKU 的选项对应）</Checkbox>
        <div v-if="specUseSchema" class="shop-schema-wrap">
          <div v-for="(g, gi) in specGroups" :key="'grp-' + gi" class="shop-group-card">
            <div class="shop-group-head">
              <span class="shop-group-title">维度 {{ gi + 1 }}</span>
              <Button type="text" size="small" @click="removeSpecGroup(gi)">删除</Button>
            </div>
            <Row :gutter="8">
              <Col :span="6">
                <FormItem :label-width="56" label="显示名" class="shop-inner-form-item">
                  <Input v-model="g.title" placeholder="如：杯型" maxlength="16" />
                </FormItem>
              </Col>
              <Col :span="6">
                <FormItem :label-width="56" label="id" class="shop-inner-form-item">
                  <Input v-model="g.id" placeholder="留空则自动根据显示名生成" maxlength="32" />
                </FormItem>
              </Col>
              <Col :span="6">
                <Checkbox v-model="g.required">必选</Checkbox>
              </Col>
              <Col :span="6">
                <Checkbox v-model="g.multi">可多选</Checkbox>
              </Col>
            </Row>
            <div class="shop-option-list">
              <div v-for="(o, oi) in g.options" :key="'opt-' + gi + '-' + oi" class="shop-option-row">
                <Input v-model="o.label" placeholder="选项显示，如：大杯" class="shop-opt-inp" maxlength="80" />
                <Input v-model="o.id" placeholder="选项 id，可空自动生成" class="shop-opt-inp" maxlength="64" />
                <Input v-model="o.sub_label" placeholder="副文案（选填）" class="shop-opt-inp" maxlength="80" />
                <Checkbox v-model="o.default">默认</Checkbox>
                <Button type="text" size="small" @click="removeSpecOption(gi, oi)">删</Button>
              </div>
              <Button type="dashed" size="small" icon="md-add" @click="addSpecOption(gi)">添加选项</Button>
            </div>
          </div>
          <div style="margin-top: 8px">
            <Button type="dashed" icon="md-add" @click="addSpecGroup">添加规格维度</Button>
            <Button style="margin-left: 8px" size="small" @click="autoFillSpecIds">自动补全维度/选项 id</Button>
          </div>
          <div class="json-field-help">
            小程序里 <code>options</code> 的键名须等于维度 <code>id</code>，键值须等于选项 <code>id</code>。
          </div>
        </div>
      </FormItem>

      <FormItem label="SKU">
        <RadioGroup v-model="skuMode" type="button">
          <Radio label="single">单规格（只用上方价格、库存）</Radio>
          <Radio label="multi">多 SKU（每种组合单独定价与库存）</Radio>
        </RadioGroup>
        <div v-if="skuMode === 'multi'" class="shop-sku-wrap">
          <div class="shop-sku-toolbar">
            <Button
              type="primary"
              ghost
              size="small"
              :disabled="!specUseSchema || !specGroups.length"
              @click="autoGenerateSkusFromSpec"
            >
              根据规格一键生成 SKU 行
            </Button>
            <Button type="dashed" size="small" style="margin-left: 8px" @click="addSkuRow">手动添加一行</Button>
          </div>
          <div v-for="(s, si) in skuRows" :key="'sku-' + si" class="shop-sku-card">
            <Row :gutter="8">
              <Col :span="6">
                <div class="shop-field-label">售卖名称</div>
                <Input v-model="s.label" placeholder="如：大杯" maxlength="120" />
              </Col>
              <Col :span="4">
                <div class="shop-field-label">价格(元)</div>
                <Input v-model="s.price" placeholder="12" maxlength="16" />
              </Col>
              <Col :span="4">
                <div class="shop-field-label">会员价</div>
                <Input v-model="s.member_price" placeholder="可选" maxlength="16" />
              </Col>
              <Col :span="3">
                <div class="shop-field-label">库存</div>
                <Input v-model.number="s.stock" type="number" min="0" placeholder="0" />
              </Col>
              <Col :span="5">
                <div class="shop-field-label">sku_id（可空自动生成）</div>
                <Input v-model="s.sku_id" placeholder="留空保存时自动生成" maxlength="64" />
              </Col>
              <Col :span="2" style="text-align: right; padding-top: 22px">
                <Button type="text" @click="removeSkuRow(si)"><Icon type="md-trash" /></Button>
              </Col>
            </Row>
            <div v-if="specUseSchema && specGroups.length" class="shop-sku-options">
              <div v-for="(g, gix) in specGroups" :key="'skg-' + si + '-' + gix" class="shop-sku-dim-row">
                <span class="shop-field-label-inline">{{ g.title || '维度' + (gix + 1) }}：</span>
                <Select
                  :value="skuDimValue(s, gix)"
                  style="width: 220px"
                  transfer
                  placeholder="选择选项"
                  @on-change="(v) => onSkuDimChange(si, gix, v)"
                >
                  <Option v-for="op in specOptionsForSelect(g)" :key="op._selKey" :value="op._selVal">
                    {{ op.label }}{{ op._selHint ? '（' + op._selHint + '）' : '' }}
                  </Option>
                </Select>
              </div>
            </div>
          </div>
          <div class="json-field-help">
            启用规格时，请为每个 SKU 在各维度下选择对应选项；也可用「一键生成」再改价与库存。
          </div>
        </div>
      </FormItem>

      <FormItem label=" ">
        <Checkbox v-model="jsonAdvancedOpen">展开：高级 JSON 编辑（解析后可覆盖上方表单）</Checkbox>
        <div v-show="jsonAdvancedOpen" class="shop-json-advanced">
          <div class="shop-json-field">
            <div class="shop-json-label">卡片标签 JSON</div>
            <Input v-model="formItem.card_tags_json" type="textarea" :autosize="{ minRows: 3, maxRows: 8 }" />
          </div>
          <div class="shop-json-field">
            <div class="shop-json-label">规格 schema JSON</div>
            <Input v-model="formItem.spec_schema_json" type="textarea" :autosize="{ minRows: 3, maxRows: 10 }" />
          </div>
          <div class="shop-json-field">
            <div class="shop-json-label">SKU 列表 JSON</div>
            <Input v-model="formItem.skus_json" type="textarea" :autosize="{ minRows: 4, maxRows: 14 }" />
          </div>
          <Button type="warning" ghost @click="applyJsonToShopEditor">将 JSON 解析并覆盖表单</Button>
        </div>
      </FormItem>

      <FormItem label="强制规格弹层" prop="show_spec_sheet">
        <i-switch v-model="formItem.show_spec_sheet" />
        <span style="margin-left: 8px; color: #999; font-size: 12px">开启后列表「+」先选规格再加车</span>
        <div class="json-field-help">
          有多个 SKU 时系统也会要求选规格；此项用于「即使只有一个 SKU 也想先弹出选规格界面」等场景，一般可关。
        </div>
      </FormItem>
      <FormItem label="分类" prop="type_id" required>
        <Select v-model="formItem.type_id" clearable placeholder="请选择分类">
          <Option
            v-for="item in typeList"
            :key="item.type_id"
            :value="item.type_id"
          >
            {{ item.name }}
          </Option>
        </Select>
      </FormItem>
      <FormItem label="上传图片" prop="img" required>
        <Upload 
          ref="addUpload" 
          :before-upload="handleUpload" 
          accept="image/jpeg,image/jpg,image/png,image/gif"
          action
        >
          <Button v-show="!imgUrl" icon="md-cloud-upload">上传图片</Button>
          <img v-show="imgUrl" :src="imgUrl" class="uploadImgUrl" />
        </Upload>
        <div style="color: #999; font-size: 12px; margin-top: 5px;">
          支持格式：JPG、JPEG、PNG、GIF，单张不超过 2MB
        </div>
      </FormItem>
    </Form>
    </div>
    <div slot="footer" style="text-align: center">
      <Button @click="cancel('formItem')">关闭</Button>
      <Button type="primary" :loading="loading" @click="ok('formItem')">
        提交
      </Button>
    </div>
  </Modal>
</template>
<script>
import { goodsUpdate, goodsAdd, goodsTypePage } from '@/api/goods'
import { imgUpload } from '@/api/upload'
import { joinMediaUrl } from '@/libs/media-url'
export default {
  name: 'Goods',
  data() {
    return {
      formItem: {
        name: '',
        price: '',
        member_price: '',
        num: '',
        desc: '',
        type_id: '',
        search_keywords: '',
        card_tags_json: '[]',
        spec_schema_json: '',
        skus_json: '[]',
        show_spec_sheet: false,
        fulfillment_scope: 'dine_in',
      },
      ruleValidate: {
        name: [
          { required: true, message: '请输入商品名称', trigger: 'blur' },
          {
            validator(rule, v, cb) {
              const name = String(v != null ? v : '').trim()
              if (!name) cb(new Error('请输入商品名称'))
              else cb()
            },
            trigger: 'blur',
          },
        ],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' },
          {
            validator(rule, v, cb) {
              const n = Number(String(v != null ? v : '').replace(/[^\d.-]/g, ''))
              if (!Number.isFinite(n) || n < 0) cb(new Error('价格须为大于等于 0 的数字'))
              else cb()
            },
            trigger: 'blur',
          },
        ],
        num: [{ validator: (r, v, cb) => this.validateNum(r, v, cb), trigger: 'change' }],
        desc: [{ required: true, message: '请输入描述', trigger: 'blur' }],
        type_id: [{ validator: (r, v, cb) => this.validateTypeId(r, v, cb), trigger: 'change' }],
        img: [{ validator: (r, v, cb) => this.validateImg(r, v, cb), trigger: 'change' }],
      },
      typeList: [],
      open: false,
      title: '商品修改',
      loading: false,
      imgUrl: '',
      uploading: false,
      /** 商超：表单化编辑 */
      tagRows: [],
      specUseSchema: false,
      specGroups: [],
      skuMode: 'single',
      skuRows: [],
      jsonAdvancedOpen: false,
    }
  },
  watch: {
    skuMode(val) {
      if (val === 'single') this.skuRows = []
    },
    specUseSchema() {
      this.ensureSkuDimArrays()
    },
    specGroups: {
      deep: true,
      handler() {
        this.ensureSkuDimArrays()
      },
    },
  },
  methods: {
    validateNum(rule, v, cb) {
      if (v === '' || v === null || v === undefined) {
        cb(new Error('请输入库存'))
        return
      }
      const n = Math.floor(Number(v))
      if (!Number.isFinite(n) || n < 0) cb(new Error('库存须为非负整数'))
      else cb()
    },
    validateTypeId(rule, v, cb) {
      const n = Number(v)
      if (!Number.isFinite(n) || n <= 0) cb(new Error('请选择商品分类'))
      else cb()
    },
    validateImg(rule, value, cb) {
      const img =
        (value != null ? String(value).trim() : '') ||
        (this.formItem.img != null ? String(this.formItem.img).trim() : '')
      if (!img) cb(new Error('请上传商品图片'))
      else cb()
    },
    /** 编辑回填时统一字段类型，避免 Input/Select 与校验状态不一致 */
    normalizeFormItemTypes() {
      const p = this.formItem
      if (!p || typeof p !== 'object') return
      if (p.price != null && p.price !== '') p.price = String(p.price)
      if (p.num != null && p.num !== '') p.num = String(p.num)
      if (p.type_id == null || p.type_id === '') {
        const ft =
          Array.isArray(p.foodtypes) && p.foodtypes[0]
            ? p.foodtypes[0]
            : null
        if (ft) {
          const tid = ft.type_id != null ? ft.type_id : ft.typeid
          if (tid != null && tid !== '') p.type_id = Number(tid)
        }
      } else {
        p.type_id = Number(p.type_id)
      }
      if (p.img != null) p.img = String(p.img).trim()
    },
    clearFormValidate() {
      this.$nextTick(() => {
        const form = this.$refs.formItem
        if (form && typeof form.clearValidate === 'function') form.clearValidate()
      })
    },
    /** 仅 1 条 SKU 时：与上方「库存」对齐，避免提交后后台按 SKU 求和把总库存写成 0 */
    syncSingleSkuStockFromRootNum() {
      if (this.skuMode !== 'multi' || this.skuRows.length !== 1) return
      const n = Math.max(0, Math.floor(Number(this.formItem.num) || 0))
      this.$set(this.skuRows[0], 'stock', n)
    },
    parseShopExtrasFromForm() {
      return this.buildShopExtrasFromState()
    },
    buildShopExtrasFromState() {
      this.syncSingleSkuStockFromRootNum()
      const err = this.validateShopEditor()
      if (err) throw new Error(err)
      const card_tags = this.tagRows
        .filter((r) => (r.text || '').trim())
        .map((r) => ({
          kind: r.kind || 'caption',
          tone: r.tone != null ? String(r.tone).trim() : '',
          text: (r.text || '').trim().slice(0, 48),
        }))
        .slice(0, 10)
      let spec_schema
      if (this.specUseSchema) {
        const built = this.buildGroupsForSubmit()
        spec_schema = built || undefined
      } else {
        spec_schema = undefined
      }
      const skus = this.buildSkusFromState()
      this.syncJsonMirrorFromState({ card_tags, spec_schema, skus })
      return {
        search_keywords: this.formItem.search_keywords,
        card_tags,
        skus,
        spec_schema,
        show_spec_sheet: !!this.formItem.show_spec_sheet,
      }
    },
    syncJsonMirrorFromState({ card_tags, spec_schema, skus }) {
      this.formItem.card_tags_json = JSON.stringify(card_tags, null, 2)
      this.formItem.spec_schema_json = spec_schema ? JSON.stringify(spec_schema, null, 2) : ''
      this.formItem.skus_json = JSON.stringify(skus, null, 2)
    },
    slugKey(s) {
      let t = String(s || '')
        .trim()
        .toLowerCase()
        .replace(/[\s/\\]+/g, '_')
        .replace(/[^a-z0-9_\-]/g, '')
        .replace(/_+/g, '_')
      t = t.slice(0, 32)
      return t || ''
    },
    makeSkuId(label, idx) {
      const b = this.slugKey(label) || 'sku'
      const tail = `${idx + 1}_${Date.now().toString(36)}`
      return (b + '_' + tail).slice(0, 64)
    },
    autoFillSpecIds() {
      this.specGroups.forEach((g, gi) => {
        if (!(g.id || '').trim()) {
          const auto = this.slugKey(g.title || `dim_${gi + 1}`)
          if (auto) this.$set(g, 'id', auto)
        }
        if (!(g.id || '').trim()) this.$set(g, 'id', `dim_${gi + 1}`)
        ;(g.options || []).forEach((o, oi) => {
          if (!(o.id || '').trim() && (o.label || '').trim()) {
            const base = this.slugKey(o.label)
            this.$set(o, 'id', base ? base + (oi ? '_' + oi : '') : `opt_${gi}_${oi}`)
          }
        })
      })
    },
    buildGroupsForSubmit() {
      this.autoFillSpecIds()
      const groups = []
      this.specGroups.forEach((g, gi) => {
        const id = ((g.id || '').trim()) || this.slugKey(g.title || `dim_${gi}`)
        const title = (g.title || '').trim()
        if (!title || !id) return
        const options = []
        ;(g.options || []).forEach((o, oi) => {
          const label = (o.label || '').trim()
          if (!label) return
          const oid = ((o.id || '').trim()) || this.slugKey(label) + (oi ? '_' + oi : '')
          options.push({
            id: oid,
            label,
            sub_label: (o.sub_label || '').trim(),
            default: !!o.default,
          })
        })
        if (!options.length) return
        groups.push({
          id,
          title,
          required: g.required !== false,
          multi: !!g.multi,
          order: Number.isFinite(Number(g.order)) ? Number(g.order) : gi,
          options,
        })
      })
      return groups.length ? { groups } : undefined
    },
    ensureSkuDimArrays() {
      const n = this.specUseSchema ? this.specGroups.length : 0
      this.skuRows.forEach((row) => {
        if (!Array.isArray(row.dimOptionIds)) this.$set(row, 'dimOptionIds', [])
        const arr = row.dimOptionIds
        while (arr.length < n) arr.push('')
        if (arr.length > n) arr.splice(n)
      })
    },
    buildSkusFromState() {
      if (this.skuMode !== 'multi' || !this.skuRows.length) return []
      this.autoFillSpecIds()
      this.ensureSkuDimArrays()
      const groups = this.specGroups
      return this.skuRows.map((row, idx) => {
        let sku_id = (row.sku_id || '').trim()
        if (!sku_id) sku_id = this.makeSkuId(row.label, idx)
        const options = {}
        if (this.specUseSchema && groups.length) {
          groups.forEach((g, gix) => {
            const gid = ((g.id || '').trim()) || this.slugKey(g.title || `dim_${gix}`)
            const pick = (row.dimOptionIds && row.dimOptionIds[gix]) || ''
            if (pick) options[gid] = String(pick).trim()
          })
        }
        return {
          sku_id,
          label: (row.label || '').trim() || sku_id,
          price: String(row.price != null ? row.price : '0'),
          member_price:
            row.member_price != null && String(row.member_price).trim() !== ''
              ? String(row.member_price).trim()
              : undefined,
          stock: Math.max(0, Math.floor(Number(row.stock) || 0)),
          options,
        }
      })
    },
    validateShopEditor() {
      if (this.skuMode === 'multi' && this.skuRows.length > 1) {
        const sum = this.skuRows.reduce((s, r) => s + Math.max(0, Math.floor(Number(r.stock) || 0)), 0)
        const root = Math.max(0, Math.floor(Number(this.formItem.num) || 0))
        if (root > 0 && sum === 0) {
          return '已填写上方总库存，但各 SKU 库存合计为 0。请在下方「库存」列为每行填写数量（保存后总库存以各 SKU 合计为准）'
        }
      }
      if (this.specUseSchema) {
        if (!this.specGroups.length) return '已勾选「启用多规格」，请至少添加一个规格维度，或取消勾选'
        for (let gi = 0; gi < this.specGroups.length; gi++) {
          const g = this.specGroups[gi]
          const title = (g.title || '').trim()
          if (!title) return `请填写规格维度 ${gi + 1} 的显示名`
          const hasOpt = (g.options || []).some((o) => (o.label || '').trim())
          if (!hasOpt) return `维度「${title}」请至少添加一个有文案的选项`
        }
      }
      if (this.skuMode === 'multi' && this.skuRows.length) {
        for (let si = 0; si < this.skuRows.length; si++) {
          const r = this.skuRows[si]
          if (!(r.label || '').trim() && !(r.sku_id || '').trim()) return `第 ${si + 1} 条 SKU 请填写售卖名称`
          if (this.specUseSchema && this.specGroups.length) {
            this.ensureSkuDimArrays()
            for (let gix = 0; gix < this.specGroups.length; gix++) {
              if (!(r.dimOptionIds[gix] || '').toString().trim()) {
                return `第 ${si + 1} 条 SKU：请为「${this.specGroups[gix].title || '维度'}」选择选项`
              }
            }
          }
        }
      }
      return ''
    },
    specOptionsForSelect(g) {
      this.autoFillSpecIds()
      const opts = (g.options || []).filter((x) => (x.label || '').trim())
      return opts.map((op, oi) => {
        const val = ((op.id || '').trim()) || this.slugKey(op.label) + (oi ? '_' + oi : '')
        return {
          _selKey: `${val}-${oi}`,
          _selVal: val,
          label: op.label,
          _selHint: val,
        }
      })
    },
    skuDimValue(s, gix) {
      if (!s.dimOptionIds) return ''
      return s.dimOptionIds[gix] == null ? '' : s.dimOptionIds[gix]
    },
    onSkuDimChange(si, gix, val) {
      const row = this.skuRows[si]
      if (!row.dimOptionIds) this.$set(row, 'dimOptionIds', [])
      this.ensureSkuDimArrays()
      this.$set(row.dimOptionIds, gix, val)
    },
    cartesian(arrays) {
      if (!arrays.length) return [[]]
      const [first, ...rest] = arrays
      if (!first.length) return []
      const tail = rest.length ? this.cartesian(rest) : [[]]
      const out = []
      for (const a of first) {
        for (const t of tail) {
          out.push(t.length ? [a, ...t] : [a])
        }
      }
      return out
    },
    autoGenerateSkusFromSpec() {
      this.autoFillSpecIds()
      const arr = this.specGroups.map((g) => (g.options || []).filter((o) => (o.label || '').trim()))
      if (arr.some((a) => !a.length)) {
        this.$Message.warning('请为每个维度至少填写一个选项文案')
        return
      }
      const combos = this.cartesian(arr)
      const basePrice = String(this.formItem.price || '0')
      this.skuMode = 'multi'
      this.skuRows = combos.map((combo, idx) => {
        const labels = combo.map((o) => (o.label || '').trim())
        const dimOptionIds = combo.map((o) => ((o.id || '').trim()) || this.slugKey(o.label))
        const label = labels.join(' ')
        return {
          sku_id: this.makeSkuId(label, idx),
          label,
          price: basePrice,
          member_price: String(this.formItem.member_price || ''),
          stock: 0,
          dimOptionIds,
        }
      })
      this.$Message.success(`已生成 ${this.skuRows.length} 条 SKU，请检查价格与库存`)
    },
    addTagRow() {
      if (this.tagRows.length >= 10) {
        this.$Message.warning('最多 10 个标签')
        return
      }
      this.tagRows.push({ text: '', tone: 'orange', kind: 'caption' })
    },
    removeTagRow(ti) {
      this.tagRows.splice(ti, 1)
    },
    addSpecGroup() {
      if (this.specGroups.length >= 12) {
        this.$Message.warning('最多 12 个规格维度')
        return
      }
      this.specGroups.push({
        id: '',
        title: '',
        required: true,
        multi: false,
        order: this.specGroups.length,
        options: [{ id: '', label: '', sub_label: '', default: false }],
      })
    },
    removeSpecGroup(gi) {
      this.specGroups.splice(gi, 1)
      this.ensureSkuDimArrays()
    },
    addSpecOption(gi) {
      const g = this.specGroups[gi]
      if (!g.options) this.$set(g, 'options', [])
      if (g.options.length >= 40) {
        this.$Message.warning('单维度最多 40 个选项')
        return
      }
      g.options.push({ id: '', label: '', sub_label: '', default: false })
    },
    removeSpecOption(gi, oi) {
      this.specGroups[gi].options.splice(oi, 1)
    },
    addSkuRow() {
      this.autoFillSpecIds()
      const n = this.specUseSchema ? this.specGroups.length : 0
      this.skuRows.push({
        sku_id: '',
        label: '',
        price: String(this.formItem.price || '0'),
        member_price: String(this.formItem.member_price || ''),
        stock: 0,
        dimOptionIds: Array(n).fill(''),
      })
    },
    removeSkuRow(si) {
      this.skuRows.splice(si, 1)
    },
    resetShopEditor() {
      this.tagRows = []
      this.specUseSchema = false
      this.specGroups = []
      this.skuMode = 'single'
      this.skuRows = []
      this.jsonAdvancedOpen = false
    },
    hydrateShopEditorFromParams(params) {
      this.resetShopEditor()
      if (!params) return
      const tags = Array.isArray(params.card_tags) ? params.card_tags : []
      this.tagRows = tags.map((t) => ({
        text: t.text || '',
        tone: t.tone != null ? String(t.tone) : '',
        kind: t.kind || 'caption',
      }))
      const sch = params.spec_schema
      const groups = sch && Array.isArray(sch.groups) ? sch.groups : []
      this.specUseSchema = groups.length > 0
      this.specGroups = groups.map((g, gi) => ({
        id: g.id || '',
        title: g.title || '',
        required: g.required !== false,
        multi: !!g.multi,
        order: g.order != null ? g.order : gi,
        options: (g.options || []).map((o) => ({
          id: o.id || '',
          label: o.label || '',
          sub_label: o.sub_label || '',
          default: !!o.default,
        })),
      }))
      const skus = Array.isArray(params.skus) ? params.skus : []
      this.skuMode = skus.length ? 'multi' : 'single'
      this.autoFillSpecIds()
      this.skuRows = skus.map((s) => {
        const dimOptionIds = this.specGroups.map((g, gix) => {
          const gid = ((g.id || '').trim()) || this.slugKey(g.title || `dim_${gix}`)
          const opts = s.options && typeof s.options === 'object' ? s.options : {}
          const hit = opts[gid]
          return hit != null ? String(hit) : ''
        })
        return {
          sku_id: s.sku_id || '',
          label: s.label || '',
          price: String(s.price != null ? s.price : ''),
          member_price: s.member_price != null ? String(s.member_price) : '',
          stock: Math.max(0, Math.floor(Number(s.stock) || 0)),
          dimOptionIds,
        }
      })
    },
    applyJsonToShopEditor() {
      try {
        const card_tags = JSON.parse(this.formItem.card_tags_json || '[]')
        const skus = JSON.parse(this.formItem.skus_json || '[]')
        const schRaw = (this.formItem.spec_schema_json || '').trim()
        const spec_schema = schRaw ? JSON.parse(schRaw) : null
        const fake = {
          card_tags: Array.isArray(card_tags) ? card_tags : [],
          skus: Array.isArray(skus) ? skus : [],
          spec_schema: spec_schema && typeof spec_schema === 'object' && !Array.isArray(spec_schema) ? spec_schema : null,
        }
        this.hydrateShopEditorFromParams(fake)
        this.$Message.success('已从 JSON 同步到表单')
      } catch (e) {
        this.$Message.error('JSON 解析失败：' + (e.message || ''))
      }
    },
    buildGoodsPayload() {
      const extras = this.parseShopExtrasFromForm()
      return {
        name: String(this.formItem.name || '').trim(),
        price: String(this.formItem.price || '0'),
        member_price: String(this.formItem.member_price || '').trim(),
        num: Number(this.formItem.num || 0),
        desc: String(this.formItem.desc || '').trim(),
        type_id: Number(this.formItem.type_id || 0),
        img: String(this.formItem.img || '').trim() || '/default-product.png',
        fulfillment_scope: String(this.formItem.fulfillment_scope || 'dine_in').trim() || 'dine_in',
        ...extras,
      }
    },
    // 初始化商品信息表单的函数
    init(params) {
      this.getTypeList()
      if (params) {
        this.title = '商品修改'
        this.formItem = JSON.parse(JSON.stringify(params))
        const p = this.formItem
        p.search_keywords = Array.isArray(params.search_keywords)
          ? params.search_keywords.join('，')
          : params.search_keywords != null
            ? String(params.search_keywords)
            : ''
        p.card_tags_json = JSON.stringify(Array.isArray(params.card_tags) ? params.card_tags : [], null, 2)
        p.spec_schema_json =
          params.spec_schema && typeof params.spec_schema === 'object'
            ? JSON.stringify(params.spec_schema, null, 2)
            : ''
        p.skus_json = JSON.stringify(Array.isArray(params.skus) ? params.skus : [], null, 2)
        p.show_spec_sheet = !!params.show_spec_sheet
        p.fulfillment_scope =
          params.fulfillment_scope === 'delivery'
            ? 'dine_in'
            : params.fulfillment_scope === 'both' || params.fulfillment_scope === 'dine_in'
              ? params.fulfillment_scope
              : 'dine_in'
        if (params.img) {
          this.imgUrl = joinMediaUrl(params.img)
        }
        this.hydrateShopEditorFromParams(params)
        this.normalizeFormItemTypes()
      } else {
        this.cancel('formItem')
        this.title = '商品添加'
      }
      this.open = true
      this.clearFormValidate()
    },

    // 获取商品类型列表的函数
    getTypeList() {
      goodsTypePage({ pageNo: 1, pageSize: 200 })
        .then((res) => {
          // 处理成功的响应
          if (res.data.code === 200) {
            console.log(res.data.data)
            this.typeList = (res.data.data || []).map((row) => ({
              ...row,
              type_id: row.type_id != null ? row.type_id : row.typeid,
            }))
          } else {
            this.$Message.error(res.data.msg) // 如果响应码不是200，显示错误消息
          }
        })
        .catch((err) => {
          // 处理错误的响应
          this.$Message.error(err) // 显示错误消息
        })
    },

    // 确认提交表单的函数
    ok(name) {
      this.normalizeFormItemTypes()
      console.log('🔵 [商品添加] 开始提交，表单引用:', name)
      console.log('🔵 [商品添加] 表单数据:', JSON.stringify(this.formItem, null, 2))

      this.$refs[name].validate((valid) => {
        console.log('🔵 [商品添加] 表单验证结果:', valid)
        
        // 调用表单验证
        if (!valid) {
          console.error('❌ [商品添加] 表单验证失败，请检查必填字段')
          this.$Message.error('请填写完整的商品信息')
          return // 如果验证失败，直接返回
        }
        
        console.log('✅ [商品添加] 表单验证通过')
        console.log('🔵 [商品添加] 是否有_id:', !!this.formItem._id)
        console.log('🔵 [商品添加] API地址:', process.env.VUE_APP_BASEURL)
        
        let basePayload
        try {
          basePayload = this.buildGoodsPayload()
        } catch (e) {
          this.$Message.error(e.message || '扩展字段解析失败')
          return
        }

        if (this.formItem._id) {
          // 如果formItem有_id属性，表示是修改操作
          console.log('🔵 [商品修改] 开始修改商品，ID:', this.formItem._id)
          const updatePayload = { _id: this.formItem._id, ...basePayload }
          goodsUpdate(updatePayload) // 调用商品修改的API
            .then((res) => {
              console.log('✅ [商品修改] API响应:', res)
              console.log('✅ [商品修改] 响应数据:', JSON.stringify(res.data, null, 2))
              // 处理成功的响应
              if (res.data.code === 200) {
                // 如果响应码为200，表示修改成功
                console.log('✅ [商品修改] 修改成功')
                this.$Message.success('商品修改成功') // 显示成功消息
                this.cancel('formItem') // 调用取消函数，重置表单
                this.$emit('reload') // 触发reload事件，通知父组件重新加载列表
              } else {
                console.error('❌ [商品修改] 业务错误，code:', res.data.code, 'msg:', res.data.msg)
                this.$Message.error(res.data.msg) // 如果响应码不是200，显示错误消息
              }
            })
            .catch((err) => {
              console.error('❌ [商品修改] 请求异常:', err)
              console.error('❌ [商品修改] 错误详情:', {
                message: err.message,
                response: err.response,
                request: err.request,
                config: err.config
              })
              // 处理错误的响应
              const errorMsg = err.response?.data?.msg || err.message || '修改失败，请检查网络连接'
              console.error('❌ [商品修改] 错误消息:', errorMsg)
              this.$Message.error(errorMsg) // 显示错误消息
            })
        } else {
          // 如果formItem没有_id属性，表示是添加操作
          console.log('🔵 [商品添加] 开始添加商品')
          console.log('🔵 [商品添加] 提交数据:', JSON.stringify(this.formItem, null, 2))
          
          // 检查图片是否正在上传
          if (this.uploading) {
            console.warn('⚠️ [商品添加] 图片正在上传中，请稍候...')
            this.$Message.warning('图片正在上传中，请稍候再提交')
            return
          }
          
          // 检查图片是否已上传
          if (!this.formItem.img || this.formItem.img.trim() === '') {
            console.warn('⚠️ [商品添加] 图片未上传，formItem.img:', this.formItem.img)
            console.warn('⚠️ [商品添加] imgUrl:', this.imgUrl)
            
            // 如果用户选择了图片但上传失败，提示用户
            if (this.imgUrl && this.imgUrl.startsWith('data:')) {
              this.$Message.warning('图片上传失败，请重新上传图片后再提交')
              return
            }
            
            // 如果图片为空，使用默认占位图（后端要求 img 字段必填）
            console.log('⚠️ [商品添加] 使用默认图片路径')
          }
          
          // 确保数据类型正确（含商超扩展字段）
          const submitData = basePayload
          
          console.log('🔵 [商品添加] 处理后的数据:', JSON.stringify(submitData, null, 2))
          console.log('🔵 [商品添加] 图片URL:', submitData.img)
          
          goodsAdd(submitData) // 调用商品添加的API
            .then((res) => {
              console.log('✅ [商品添加] API响应成功')
              console.log('✅ [商品添加] 完整响应:', res)
              console.log('✅ [商品添加] 响应数据:', JSON.stringify(res.data, null, 2))
              console.log('✅ [商品添加] 响应状态码:', res.status)
              
              // 处理成功的响应
              if (res.data && res.data.code === 200) {
                // 如果响应码为200，表示添加成功
                console.log('✅ [商品添加] 添加成功！')
                this.$Message.success('商品添加成功') // 显示成功消息
                this.cancel('formItem') // 调用取消函数，重置表单
                this.$emit('reload') // 触发reload事件，通知父组件重新加载列表
              } else {
                console.error('❌ [商品添加] 业务错误')
                console.error('❌ [商品添加] code:', res.data?.code)
                console.error('❌ [商品添加] msg:', res.data?.msg)
                const errorMsg = res.data?.msg || '添加失败，未知错误'
                this.$Message.error(errorMsg) // 如果响应码不是200，显示错误消息
              }
            })
            .catch((err) => {
              console.error('❌ [商品添加] 请求异常')
              console.error('❌ [商品添加] 错误对象:', err)
              console.error('❌ [商品添加] 错误类型:', typeof err)
              console.error('❌ [商品添加] 错误消息:', err.message)
              console.error('❌ [商品添加] 错误响应:', err.response)
              console.error('❌ [商品添加] 错误请求:', err.request)
              
              // 处理错误的响应
              let errorMsg = '添加失败'
              
              if (err.response) {
                // 服务器返回了错误响应
                console.error('❌ [商品添加] 服务器错误响应')
                console.error('❌ [商品添加] 状态码:', err.response.status)
                console.error('❌ [商品添加] 响应数据:', err.response.data)
                errorMsg = err.response.data?.msg || `服务器错误 (${err.response.status})`
              } else if (err.request) {
                // 请求已发送但没有收到响应
                console.error('❌ [商品添加] 请求已发送但无响应')
                console.error('❌ [商品添加] 请求对象:', err.request)
                errorMsg = '网络错误，请检查网络连接或后端服务是否运行'
              } else {
                // 其他错误
                console.error('❌ [商品添加] 其他错误:', err.message)
                errorMsg = err.message || '未知错误'
              }
              
              console.error('❌ [商品添加] 最终错误消息:', errorMsg)
              this.$Message.error(errorMsg) // 显示错误消息
            })
        }
      })
    },

    // 取消操作并关闭模态框的函数
    cancel(name) {
      this.formItem = {
        name: '',
        price: '',
        member_price: '',
        num: '',
        desc: '',
        type_id: '',
        search_keywords: '',
        card_tags_json: '[]',
        spec_schema_json: '',
        skus_json: '[]',
        show_spec_sheet: false,
        fulfillment_scope: 'dine_in',
      }
      this.resetShopEditor()
      this.imgUrl = '' // 清空图片预览地址
      this.uploading = false // 重置上传状态
      this.$refs[name].resetFields() // 调用表单的resetFields方法，重置表单验证状态
      this.open = false // 设置模态框为关闭状态
    },
    handleUpload(file) {
      if (file) {
        // 检查文件大小
        if (file.size > 2 * 1024 * 1024) {
          this.$Message.error('图片过大，请选择小于 2MB 的图片')
          this.$refs['addUpload'].clearFiles()
          return false
        }
        
        // 检查文件类型
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif']
        
        // 获取文件扩展名
        const fileName = file.name || ''
        const fileExtension = fileName.split('.').pop()?.toLowerCase() || ''
        
        console.log('🖼️ [图片上传] 文件信息:', {
          name: file.name,
          type: file.type,
          size: file.size,
          extension: fileExtension
        })
        
        // 验证文件类型
        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
          const errorMsg = `文件格式不正确，仅支持 JPG、JPEG、PNG、GIF 格式。当前文件：${file.name || '未知文件'}`
          console.error('❌ [图片上传] 文件格式验证失败:', {
            fileType: file.type,
            fileExtension: fileExtension,
            allowedTypes: allowedTypes,
            allowedExtensions: allowedExtensions
          })
          this.$Message.error(errorMsg)
          this.$refs['addUpload'].clearFiles()
          return false
        }
        
        console.log('🖼️ [图片上传] 开始上传，文件大小:', file.size, 'bytes')
        this.uploading = true // 设置上传状态
        
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          const _base64 = reader.result
          this.imgUrl = _base64 //将_base64赋值给图片的src，实现图片预览
          console.log('🖼️ [图片上传] 预览图片已设置')
        }
        
        let formData = new FormData()
        formData.append('avatar', file)
        
        console.log('🖼️ [图片上传] 发送上传请求...')
        imgUpload(formData)
          .then((res) => {
            console.log('🖼️ [图片上传] 上传响应:', res)
            console.log('🖼️ [图片上传] 响应数据:', JSON.stringify(res.data, null, 2))
            
            if (res.data && res.data.code === 200) {
              // 获取图片URL
              const imgUrl = res.data.imgUrl || res.data.data
              console.log('✅ [图片上传] 上传成功，图片URL:', imgUrl)
              
              if (imgUrl) {
                this.formItem.img = imgUrl
                console.log('✅ [图片上传] formItem.img 已设置:', this.formItem.img)
                this.$Message.success('图片上传成功')
                this.$nextTick(() => {
                  try {
                    this.$refs.formItem && this.$refs.formItem.validateField('img')
                  } catch (_) {}
                })
              } else {
                console.error('❌ [图片上传] 响应中没有 imgUrl 字段')
                console.error('❌ [图片上传] 完整响应:', res.data)
                this.$Message.error('上传成功但未获取到图片地址')
              }
            } else {
              const st = res.status
              const body = res.data
              const bodyStr = typeof body === 'string' ? body : ''
              let errorMsg =
                typeof body === 'object' && body && body.msg ? body.msg : '上传失败'
              if (
                st === 413 ||
                bodyStr.includes('413') ||
                bodyStr.includes('Request Entity Too Large')
              ) {
                errorMsg =
                  '网关限制：单请求体积超过 Nginx 上限（常见默认 1MB）。请将图片压到 1MB 内，或在服务器 Nginx 中设置 client_max_body_size 8m 并重载。参考后端 deploy/nginx-client-max-body.example.conf'
              }
              console.error('❌ [图片上传] 上传失败:', errorMsg)
              console.error('❌ [图片上传] 响应数据:', res.data)
              this.$Message.error(errorMsg)
              this.imgUrl = ''
              this.formItem.img = ''
              this.$refs['addUpload'].clearFiles()
            }
            this.uploading = false // 重置上传状态
          })
          .catch((err) => {
            console.error('❌ [图片上传] 上传异常:', err)
            console.error('❌ [图片上传] 错误详情:', {
              message: err.message,
              response: err.response,
              request: err.request
            })
            
            const errorMsg = err.response?.data?.msg || err.message || '图片上传失败，请检查网络连接'
            console.error('❌ [图片上传] 错误消息:', errorMsg)
            this.$Message.error(errorMsg)
            
            // 清除图片相关数据
            this.imgUrl = ''
            this.formItem.img = ''
            this.$refs['addUpload'].clearFiles()
            this.uploading = false // 重置上传状态
          })
        return false //阻止图片继续上传，使得form表单提交时统一上传
      }
      return false
    },
  },
  mounted() {},
}
</script>
<style lang="less" scoped>
.uploadImgUrl {
  width: 50px;
  height: 50px;
}
.json-field-help {
  color: #666;
  font-size: 12px;
  line-height: 1.65;
  margin-top: 8px;
  max-width: 100%;
}
.json-field-help code {
  background: #f4f4f5;
  padding: 0 4px;
  border-radius: 2px;
  font-size: 11px;
}
.json-example {
  margin: 8px 0 0;
  padding: 10px 12px;
  background: #f8f8f9;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.5;
  color: #333;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
.goods-add-modal {
  .goods-modal-body {
    max-height: calc(100vh - 220px);
    overflow-y: auto;
    padding-right: 4px;
  }
  .shop-row-block {
    margin-bottom: 8px;
  }
  .shop-schema-wrap {
    margin-top: 8px;
  }
  .shop-group-card {
    border: 1px solid #e8eaec;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;
    background: #fafafa;
  }
  .shop-group-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  .shop-group-title {
    font-weight: 600;
    color: #333;
  }
  .shop-inner-form-item {
    margin-bottom: 0;
  }
  .shop-option-list {
    margin-top: 8px;
  }
  .shop-option-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .shop-opt-inp {
    width: 160px;
  }
  .shop-sku-wrap {
    margin-top: 12px;
  }
  .shop-sku-toolbar {
    margin-bottom: 10px;
  }
  .shop-sku-card {
    border: 1px solid #e8eaec;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 10px;
    background: #fff;
  }
  .shop-field-label {
    font-size: 12px;
    color: #808695;
    margin-bottom: 4px;
  }
  .shop-field-label-inline {
    font-size: 12px;
    color: #515a6e;
    margin-right: 6px;
  }
  .shop-sku-options {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #e8eaec;
  }
  .shop-sku-dim-row {
    margin-bottom: 8px;
  }
  .shop-json-advanced {
    margin-top: 12px;
    padding: 12px;
    background: #f8f8f9;
    border-radius: 4px;
  }
  .shop-json-field {
    margin-bottom: 12px;
  }
  .shop-json-label {
    font-size: 12px;
    color: #515a6e;
    margin-bottom: 6px;
  }
}
/* 必填红色星号（与 View Design 默认一致，部分主题可加强） */
.goods-add-modal ::v-deep(.ivu-form-item-required .ivu-form-item-label:before) {
  color: #ed4014;
}
</style>
